import React from "react";
import { Trash2, Star } from "lucide-react";
import config from "../../../config";
import { RentNowButton } from "../homePageComponents/RentNowButton";
import { AddToWishlistButton } from "../homePageComponents/ButtonComponents";
import { useNavigate } from "react-router-dom";
import RatingStars from "../homePageComponents/RatingStars";
import toast from "react-hot-toast";
import ReminderManager from "../../../services/home/ReminderManager";
import { useSelector } from "react-redux";

/**
 * CartItem Component
 *
 * Props:
 * - book: {
 *     id,
 *     title,
 *     author,
 *     publisher,
 *     isbn,
 *     imageUrl,
 *     rating (0-5)
 *   }
 * - onWishlist: (bookId) => void
 * - onRemove: (bookId) => void
 * - onRent: (bookId) => void
 */
export default function CartItem({ book, onWishlist, onRemove, onRent }) {
  const stars = Array.from({ length: 5 }, (_, i) => i + 1);
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);

  const handleReminder = async () => {
    if (!book?.id) return;
    try {
      if (!userId) {
        toast.error("Please login to set a reminder.");
        return;
      }
      const res = await ReminderManager.createReminder(userId, book.id);
      toast.success("Reminder set successfully!");
    } catch (error) {
      console.error("Failed to set reminder:", error);
      toast.error("Failed to set reminder.");
    }
  };

  return (
    <div className="relative flex items-center bg-white dark:bg-gray-800 border border-primary-shade1 rounded-lg shadow hover:shadow-lg transition-shadow p-4 space-x-4">
      {/* Heart and Trash buttons at top right */}
      <div className="absolute top-2 right-10 flex space-x-3">
        <AddToWishlistButton book={book} />

        <button
          onClick={() => onRemove(book.id)}
          className="flex items-center text-red-500 hover:text-red-600 transition-colors"
          aria-label="Remove item"
        >
          <Trash2 size={20} />
        </button>
      </div>

      {/* Book Cover */}
      <img
        src={
          book.imageUrl && book.imageUrl !== "defaultImage.jpg"
            ? `${config.url}${book.imageUrl}`
            : "/images/defaultBook.png"
        }
        alt={book.title}
        className="w-24 h-36 object-cover rounded-lg flex-shrink-0 shadow-md cursor-pointer"
        onClick={() => navigate(`/book/${book.id}`)}
      />

      {/* Details & Actions */}
      <div className="flex-1 flex flex-col justify-between h-full">
        <div>
          <div
            className="cursor-pointer"
            onClick={() => navigate(`/book/${book.id}`)}
          >
            <h2 className="text-xl font-semibold text-text.muted dark:text-text.white line-clamp-2">
              {book.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              <span className="font-medium">Author:</span> {book.author}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              <span className="font-medium">Publisher:</span> {book.publisher}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              <span className="font-medium">ISBN:</span> {book.isbn}
            </p>
            <p className="text-sm mt-1">
              <span className="font-medium">Stock:</span>{" "}
              <span
                className={
                  book.stock?.toLowerCase() === "in_stock"
                    ? "text-green-600 font-semibold"
                    : book.stock?.toLowerCase() === "limited_stock"
                    ? "text-orange-500 font-semibold"
                    : "text-red-500 font-semibold"
                }
              >
                {book.stock?.toLowerCase() === "in_stock"
                  ? "In Stock"
                  : book.stock?.toLowerCase() === "limited_stock"
                  ? "Limited Stock"
                  : "Out of Stock"}
              </span>
            </p>

            {/* Rating */}
            <div className="flex items-center mt-2">
              <RatingStars rating={book.rating} size={16} />
            </div>
          </div>
        </div>
        <div className="absolute bottom-2 right-2 w-[80px] text-center text-xs font-medium">
          {["in_stock", "limited_stock"].includes(book.stock?.toLowerCase()) ? (
            <RentNowButton
              book={book}
              className="w-full h-8 text-[12px] leading-none"
            />
          ) : (
            <button
            onClick={handleReminder}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-white text-[12px] font-semibold py-1.5 px-4 rounded-md transition"
          >
            Remind Me
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
